import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  User,
  Stethoscope,
  Package,
  Receipt,
  Calendar,
  Clock,
  ArrowLeft,
  Download,
  Mail,
  Phone,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import storefrontCheckoutService from '@/services/storefrontCheckoutService';

const PrescriptionConfirmation: React.FC = () => {
  const { prescriptionId } = useParams<{ prescriptionId: string }>();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (prescriptionId) {
      loadPrescriptionDetails();
    }
  }, [prescriptionId]);

  const loadPrescriptionDetails = async () => {
    try {
      setLoading(true);
      const details = await storefrontCheckoutService.getPrescriptionConfirmation(prescriptionId!);
      setPrescription(details);
    } catch (error: any) {
      setError(error.message || 'Failed to load prescription details');
      toast({
        title: 'Error',
        description: 'Failed to load prescription details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = () => {
    // Implementation for downloading receipt
    toast({
      title: 'Download Started',
      description: 'Your prescription receipt is being downloaded.',
    });
  };

  const handleEmailReceipt = () => {
    // Implementation for emailing receipt
    toast({
      title: 'Email Sent',
      description: 'Order receipt has been sent to your email.',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading prescription details...</p>
        </div>
      </div>
    );
  }

  if (error || !prescription) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center overflow-auto h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Order Not Found</h2>
            <p className="text-gray-600 mb-6">
              {error || 'The order you\'re looking for could not be found.'}
            </p>
            <Button onClick={() => navigate('/storefront')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Storefront
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 overflow-auto h-screen">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/storefront')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Storefront
          </Button>
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            </motion.div>
            <h1 className="text-3xl font-bold text-green-600 mb-2">
              Prescription Created Successfully!
            </h1>
            <p className="text-gray-600">
              Your prescription has been submitted and payment processed.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Receipt className="h-5 w-5" />
                    <span>Order Details</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Prescription ID and Status */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Order ID</div>
                        <div className="text-lg font-mono text-blue-600">
                          {prescription.prescription_id}
                        </div>
                      </div>
                      <Badge
                        variant={prescription.status === 'active' ? 'default' : 'secondary'}
                        className="capitalize"
                      >
                        {prescription.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Patient and Practitioner Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                      <User className="h-8 w-8 text-blue-600" />
                      <div>
                        <div className="font-medium">Patient</div>
                        <div className="text-sm text-gray-600">{prescription.patient.full_name}</div>
                        <div className="text-xs text-gray-500">{prescription.patient.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                      <Stethoscope className="h-8 w-8 text-green-600" />
                      <div>
                        <div className="font-medium">Practitioner</div>
                        <div className="text-sm text-gray-600">{prescription.practitioner.full_name}</div>
                        {prescription.practitioner.specialty && (
                          <Badge variant="secondary" className="text-xs mt-1">
                            {prescription.practitioner.specialty}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Medications */}
                  <div>
                    <h4 className="font-medium mb-4">Prescribed Medications</h4>
                    <div className="space-y-3">
                      {prescription.medications.map((medication, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium">{medication.medication_name}</div>
                            <div className="text-sm text-gray-600">
                              Dose: {medication.dose} • Quantity: {medication.quantity}
                            </div>
                          </div>
                          <Badge
                            variant={medication.status === 'active' ? 'default' : 'secondary'}
                            className="capitalize"
                          >
                            {medication.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h4 className="font-medium mb-4">Timeline</h4>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Created on {new Date(prescription.created_at).toLocaleDateString()}</span>
                      <Clock className="h-4 w-4 ml-4" />
                      <span>{new Date(prescription.created_at).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button onClick={handleDownloadReceipt} variant="outline" className="flex-1">
                      <Download className="h-4 w-4 mr-2" />
                      Download Receipt
                    </Button>
                    <Button onClick={handleEmailReceipt} variant="outline" className="flex-1">
                      <Mail className="h-4 w-4 mr-2" />
                      Email Receipt
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    Need help? Contact support at{' '}
                    <a href="tel:+1234567890" className="text-blue-600 hover:underline">
                      (123) 456-7890
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Payment Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="sticky top-8">
                <CardHeader>
                  <CardTitle>Payment Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Amount Paid</span>
                      <span>${prescription.payment.amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Payment Status</span>
                      <Badge
                        variant={prescription.payment.status === 'completed' ? 'default' : 'secondary'}
                        className="capitalize"
                      >
                        {prescription.payment.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Transaction ID</span>
                      <span className="font-mono text-xs">{prescription.payment.transaction_id}</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-2 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Payment Successful</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Your prescription is now being processed.
                    </p>
                  </div>

                  <div className="text-xs text-gray-500">
                    You will receive email updates on your prescription status.
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionConfirmation; 